import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import Form from "@/components/Form";
import { List } from "@/components/list/List";
import SaveButton from "@/components/SaveButton";
import Spinner from "@/components/Spinner";
import Title from "@/components/Title";
import {
  Module,
  PATH_PAYROLL,
  PATH_PAYROLL_INSPECTION_DETAIL,
} from "@/constants";
import useQueryData from "@/hooks/useQueryData";
import { useSyncFormWithQueryData } from "@/hooks/useSyncFormWithQueryData";
import { usePayrollDetails } from "@/modules/payrolls/hooks/usePayrollDetails";
import { usePayrollInspectionColumnOverrides } from "@/modules/payrolls/hooks/usePayrollInspectionColumnOverrides";
import { useUpdatePayrollInspectionsMutation } from "@/modules/payrolls/hooks/useUpdatePayrollInspectionsMutation";
import { isProjectRoleDependent } from "@/modules/projects/lib/isProjectRoleDependent";
import { payrollDepartmentBasedInspection } from "@/schemas/payrollDepartmentBasedInspection";
import { payrollRoleBasedInspection } from "@/schemas/payrollRoleBasedInspection";
import { FetchListRequest } from "@/services/api";
import { PayrollsService } from "@/services/payrolls";
import { useFormBaseline } from "@/hooks/useFormBaseline";
import { useFormDefaultValuesFromQuery } from "@/hooks/useFormDefaultValuesFromQuery";
import { zodResolver } from "@hookform/resolvers/zod";
import { memo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { generatePath, Navigate } from "react-router-dom";
import { usePayrollBreadcrumbs } from "../hooks/usePayrollBreadcrumbs";
import { usePayrollParams } from "../hooks/usePayrollParams";
import { usePayrollQuery } from "../hooks/usePayrollQuery";
import { initialColumnVisibility } from "../initialColumnVisibility";
import {
  writePayrollDepartmentBasedInspectionFormSchema,
  writePayrollRoleBasedInspectionFormSchema,
} from "../schema";
import {
  PayrollInspection,
  UpdatePayrollInspection,
  WritePayrollDepartmentBasedInspectionForm,
  WritePayrollInspectionForm,
} from "../types";
import { convertFormToUpdatePayrollDetails } from "../utils/convertFormToUpdatePayrollDetails";
import { PayrollInspectionRecordView } from "./PayrollInspectionRecordView";
import { usePayrollPermissions } from "../hooks/usePayrollPermissions";
import { PayrollCreateEntryButton } from "./PayrollCreateEntryButton";
import { PayrollCorrectionButton } from "./PayrollCorrectionButton";

const payrollRoleBasedInspectionDisplaySchema = payrollRoleBasedInspection
  .unwrap()
  .omit({
    contractor_card_mimetype: true,
    contractor_card_filename: true,
  })
  .readonly();

const defaultValues: WritePayrollDepartmentBasedInspectionForm = {
  entries: {},
};

export const PayrollInspectionDirectory = memo(() => {
  const { t } = useTranslation();

  const { payrollId } = usePayrollParams();
  const payrollQuery = usePayrollQuery(payrollId);
  const isRoleBased = isProjectRoleDependent(payrollQuery.data);

  const permissions = usePayrollPermissions();

  const fetchListFn = useCallback(
    (params: FetchListRequest<PayrollInspection>) =>
      PayrollsService.getInspectionList(
        payrollId,
        payrollQuery.data?.project_depends_on,
        params,
      ),
    [payrollId, payrollQuery.data?.project_depends_on],
  );

  const pathBuilder = useCallback(
    (row: PayrollInspection) =>
      generatePath(PATH_PAYROLL_INSPECTION_DETAIL, {
        payrollId,
        entryId: row.id,
      }),
    [payrollId],
  );

  const { name, options, query, queryValue } =
    usePayrollDetails<PayrollInspection>({
      enabled: payrollQuery.isSuccess,
      fetchListFn,
      nameSuffix: "inspection",
    });

  const breadcrumbs = usePayrollBreadcrumbs([
    {
      label: t("payrolls.views.inspection.title"),
    },
  ]);

  const columnOverrides = usePayrollInspectionColumnOverrides(isRoleBased);

  const schema = isRoleBased
    ? payrollRoleBasedInspectionDisplaySchema
    : payrollDepartmentBasedInspection;

  const formSchema = isRoleBased
    ? writePayrollRoleBasedInspectionFormSchema
    : writePayrollDepartmentBasedInspectionFormSchema;

  const mutation = useUpdatePayrollInspectionsMutation(
    payrollQuery.data?.project_depends_on,
  );

  const form = useForm<WritePayrollInspectionForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
    disabled: !permissions.canUpdateInspection || mutation.isPending,
  });

  const data = useQueryData(query);
  const formDefaultValues = useFormDefaultValuesFromQuery<
    PayrollInspection,
    "entries",
    WritePayrollInspectionForm
  >(data);
  const { baseline, updateBaseline } = useFormBaseline(form, formDefaultValues);

  const handleSubmit = useCallback(
    async (values: WritePayrollInspectionForm) => {
      const dirtyFields = form.formState.dirtyFields;
      const payload =
        convertFormToUpdatePayrollDetails<UpdatePayrollInspection>(
          values,
          dirtyFields,
        );
      await mutation.mutateAsync(payload);
      updateBaseline();
    },
    [form.formState.dirtyFields, mutation, updateBaseline],
  );

  useSyncFormWithQueryData(data, form);

  if (payrollQuery.isLoading) {
    return <Spinner className="w-full h-full" />;
  }

  if (!permissions.canAccessInspection) {
    return (
      <Navigate
        to={generatePath(PATH_PAYROLL, {
          payrollId,
        })}
        replace
      />
    );
  }

  return (
    <List
      schema={schema}
      name={name}
      pathBuilder={pathBuilder}
      query={query}
      options={options}
      columnOverrides={columnOverrides}
      tableFeatures={{
        editionHighlight: { baseline },
        initialColumnVisibility,
        exportSettings: {
          moduleName: Module.Payrolls,
          queryParams: {
            payroll_stage: isRoleBased
              ? "inspection-role-based"
              : "inspection-department-based",
            payroll_id: payrollId,
          },
        },
      }}
    >
      <List.Header
        breadcrumbLabel={`${name}.title.multiple`}
        breadcrumbs={breadcrumbs}
        actions={[permissions.canCorrect && <PayrollCorrectionButton />]}
      />
      <Title>{t("payrolls.views.inspection.title")}</Title>
      <PayrollInspectionRecordView />
      <List.Menubar>
        <List.Menubar.Tools />
        <List.Menubar.Columns />
        <List.Menubar.Reset />
      </List.Menubar>
      <DataTableQueryContextProvider value={queryValue}>
        <Form form={form} handleSubmit={handleSubmit}>
          <List.Content>
            <List.Status>
              <List.DirtyRowsStatus />
            </List.Status>
          </List.Content>
          <footer className="flex py-2 justify-between gap-6">
            <PayrollCreateEntryButton />
            <SaveButton />
          </footer>
        </Form>
      </DataTableQueryContextProvider>
    </List>
  );
});
